import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { MediaMatcher } from '@angular/cdk/layout';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Pagination, PaymentHistoryResponse } from '@cargos/sprintpay-models';
import { ExternalLinksModeService } from '@cargos/sprintpay-services';
import dayjs from 'dayjs';
import { BehaviorSubject, Observable, Subject, catchError, finalize, map, merge, of, switchMap, takeUntil } from 'rxjs';
import { ColumnsType } from 'src/app/shared/models';
import { PaymentsFacilityService } from '../../services/payments.service';
import { DownloadModalComponent } from './download-modal/download-modal.component';
import { DateRangeFormType } from './models/date-range';
import { SearchTypesType } from './models/search-types';
@Component({
    selector: 'app-payment-history',
    templateUrl: './payment-history.component.html',
    styleUrls: ['./payment-history.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false,
})
export class PaymentHistoryComponent implements OnInit, OnDestroy, AfterViewInit {
    private rangeFormatAPI = 'YYYY-MM-DD';
    private filterDateRange = 'MM-DD-YYYY';
    private unsubscribe$: Subject<void>;
    private typeOfSearch = new BehaviorSubject<SearchTypesType>(SearchTypesType.DEFAULT);
    public data: MatTableDataSource<PaymentHistoryResponse, MatPaginator>;
    public resultsLength = 0;
    public isLoadingResults = true;
    public isSearching = false;
    public isRateLimitReached = false;
    public displayedColumns: string[];
    public columns: ColumnsType<PaymentHistoryResponse>[];
    public selection = new SelectionModel<PaymentHistoryResponse>(true, []);
    public itemsSelected = 0;
    public searchValue = '';
    public rangeDates: DateRangeFormType = {
        start: dayjs().format(this.rangeFormatAPI),
        end: dayjs().format(this.rangeFormatAPI),
    };
    public mobileQuery: MediaQueryList;
    private _mobileQueryListener: () => void;
    public filter = '';

    @Input()
    set columnsTable(columns: ColumnsType<PaymentHistoryResponse>[]) {
        this.columns = columns;

        this.displayedColumns = ['select', ...this.columns.map((c) => c.columnDef), 'actions'];
    }

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    constructor(
        private paymentsFacilityService: PaymentsFacilityService,
        private currencyPipe: CurrencyPipe,
        private datePipe: DatePipe,
        private matDialog: MatDialog,
        private media: MediaMatcher,
        private changeDetectorRef: ChangeDetectorRef,
        private externalLinksModeService: ExternalLinksModeService
    ) {
        this.unsubscribe$ = new Subject<void>();

        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    }

    ngOnInit(): void {
        this.subscribeWindowSize();
        this.setDisabledExternalLinksView();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
    }

    ngAfterViewInit(): void {
        this.resetPageIfSortChange();
        this.getHistory();
    }

    getHistory(): void {
        merge(this.sort.sortChange, this.paginator.page, this.getTypeOfSeach())
            .pipe(
                switchMap(() => {
                    this.isLoadingResults = true;

                    return this.searchByType();
                }),
                map((data) => {
                    this.isLoadingResults = false;
                    this.isRateLimitReached = data?.content === null;

                    if (data === null) {
                        return [];
                    }

                    this.resultsLength = data?.totalElements || 0;

                    return data?.content;
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((data) => {
                this.data = new MatTableDataSource<PaymentHistoryResponse>(data);
            });
    }

    resetPageIfSortChange(): void {
        this.sort.sortChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.paginator.pageIndex = 0;
        });
    }

    searchPaymentsByTextFilter(): Observable<Pagination<PaymentHistoryResponse> | null> {
        const sort = this.getActiveSort();
        this.isSearching = true;

        return this.paymentsFacilityService
            .searchTerms({
                searchTerms: this.searchValue,
                page: this.paginator.pageIndex,
                pageSize: this.paginator.pageSize,
                date: dayjs().valueOf(),
                sort: sort || undefined,
            })
            .pipe(
                finalize(() => {
                    this.isSearching = false;
                }),
                catchError(() => of(null))
            );
    }

    searchPaymentsByRangeDate(): Observable<Pagination<PaymentHistoryResponse> | null> {
        const sort = this.getActiveSort();

        return this.paymentsFacilityService
            .getCompletedPayments({
                page: this.paginator.pageIndex,
                pageSize: this.paginator.pageSize,
                startDate: dayjs(this.rangeDates.start).format(this.rangeFormatAPI),
                endDate: dayjs(this.rangeDates.end).format(this.rangeFormatAPI),
                sort: sort || undefined,
            })
            .pipe(catchError(() => of(null)));
    }

    searchByType(): Observable<Pagination<PaymentHistoryResponse> | null> {
        switch (this.typeOfSearch.value) {
            case SearchTypesType.SEARCH:
                this.filter = this.searchValue;

                return this.searchPaymentsByTextFilter();
            case SearchTypesType.DATE_RANGE:
                this.filter = `${dayjs(this.rangeDates.start).format(
                    this.filterDateRange
                )}/${dayjs(this.rangeDates.end).format(this.filterDateRange)}`;

                return this.searchPaymentsByRangeDate();
            default:
                this.filter = `${dayjs(this.rangeDates.start).format(
                    this.filterDateRange
                )}/${dayjs(this.rangeDates.end).format(this.filterDateRange)}`;

                return this.searchPaymentsByRangeDate();
        }
    }

    getTypeOfSeach(): Observable<SearchTypesType> {
        return this.typeOfSearch.asObservable();
    }

    getActiveSort(): string | null {
        const activeSort = this.sort.active;
        const directionSort = this.sort.direction && this.sort.direction.toUpperCase();

        return activeSort && directionSort ? activeSort + '-' + directionSort : null;
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.data?.data.length;

        return numSelected === numRows;
    }

    toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }

        this.selection.select(...this.data?.data);
    }

    checkboxLabel(row?: PaymentHistoryResponse): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.awb}`;
    }

    donwloadMultipleFiles(): void {
        this.matDialog.open(DownloadModalComponent, {
            id: 'downloadMultipleFiles',
            width: this.mobileQuery.matches ? 'auto' : '700px',
            maxWidth: '-webkit-fill-available',
            height: this.mobileQuery.matches ? '100vh' : 'auto',
            closeOnNavigation: true,
            data: {
                paymentsIDs: this.selection.selected,
            },
        });
    }

    eventOnSubmitSearch(searchValue: string): void {
        this.searchValue = searchValue;
        this.paginator.pageIndex = 0;
        this.typeOfSearch.next(SearchTypesType.SEARCH);
    }

    eventOnResetField(): void {
        this.searchValue = '';
        this.paginator.pageIndex = 0;
        this.typeOfSearch.next(SearchTypesType.DEFAULT);
    }

    refresh(): void {
        this.paginator.pageIndex = 0;
        this.typeOfSearch.next(SearchTypesType.DEFAULT);
    }

    eventOnChangeRange(dates: DateRangeFormType): void {
        this.rangeDates = dates;
        this.paginator.pageIndex = 0;
        this.typeOfSearch.next(SearchTypesType.DATE_RANGE);
    }

    eventInitialRange(dates: DateRangeFormType): void {
        this.rangeDates = dates;
    }

    dataAccessor = (
        columnsType: ColumnsType<PaymentHistoryResponse>,
        data: PaymentHistoryResponse
    ): string | HTMLAnchorElement => {
        let result = columnsType.cell(data);

        switch (columnsType.pipe) {
            case 'currency':
                result = this.currencyPipe.transform(columnsType.cell(data)) || '';
                break;
            case 'date':
                result = this.datePipe.transform(columnsType.cell(data), 'MM/dd/yyyy') || '';
                break;
        }

        return result || '';
    };

    subscribeWindowSize(): void {
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    }

    setDisabledExternalLinksView(): void {
        if (this.externalLinksModeService.isExternalLinksDisabled()) {
            this.displayedColumns = this.displayedColumns?.filter((column) => column !== 'actions');
        }
    }
}
